/**
 * DashboardDetailsPreview – Shows questions and filters for the selected dashboard.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, RefreshCw, AlertCircle, BarChart2, SlidersHorizontal, Pencil, X } from 'lucide-react';
import { useState } from 'react';
import type { MetabaseDashboardDetails, MetabaseDashboardFilter } from '@/types/dashboardConfig';

type FilterValueSource = NonNullable<MetabaseDashboardFilter['valueSource']>;

interface DashboardDetailsPreviewProps {
  details: MetabaseDashboardDetails | null;
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
  /** Per-filter static text values keyed by slug. */
  filterValues?: Record<string, string>;
  onFilterValueChange?: (slug: string, value: string) => void;
  /** Per-filter source-mapping keyed by slug. */
  filterSources?: Record<string, FilterValueSource>;
  onFilterSourceChange?: (slug: string, source: FilterValueSource | undefined) => void;
}

const SOURCE_OPTIONS: { value: FilterValueSource; label: string }[] = [
  { value: 'emailAddress', label: 'User Email Address' },
  { value: 'fullName', label: 'User Full Name' },
  { value: 'userId', label: 'User ID' },
  { value: 'static', label: 'Static Value' },
];

export function DashboardDetailsPreview({
  details,
  isLoading,
  error,
  onRetry,
  filterValues,
  onFilterValueChange,
  filterSources,
  onFilterSourceChange,
}: DashboardDetailsPreviewProps) {
  const [openFilterSlugs, setOpenFilterSlugs] = useState<Record<string, boolean>>({});
  const toggleFilterOpen = (slug: string) =>
    setOpenFilterSlugs((prev) => ({ ...prev, [slug]: !prev[slug] }));
  const editable =
    typeof onFilterValueChange === 'function' &&
    typeof onFilterSourceChange === 'function';
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-4 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading dashboard details…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span className="flex-1">{error}</span>
        <Button size="sm" variant="ghost" className="shrink-0 h-7 px-2" onClick={onRetry}>
          <RefreshCw className="h-3.5 w-3.5 mr-1" />
          Retry
        </Button>
      </div>
    );
  }

  if (!details) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Questions */}
      <Card className="border border-border">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <BarChart2 className="h-4 w-4 text-primary" />
            Questions
            <Badge variant="secondary" className="ml-auto text-xs">
              {details.questions.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 space-y-1 max-h-48 overflow-y-auto">
          {details.questions.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">No questions found.</p>
          ) : (
            details.questions.map((q) => (
              <div key={q.id} className="flex items-center gap-2 text-xs py-1">
                <span className="font-mono text-muted-foreground w-10 shrink-0">#{q.id}</span>
                <span className="text-foreground truncate">{q.name}</span>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="border border-border">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <SlidersHorizontal className="h-4 w-4 text-primary" />
            Filters
            <Badge variant="secondary" className="ml-auto text-xs">
              {details.filters.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 space-y-2 max-h-64 overflow-y-auto">
          {details.filters.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">No filters found.</p>
            ) : (
              details.filters.map((f) => {
                const source = filterSources?.[f.slug];
                const isOpen = !!openFilterSlugs[f.slug];
                const sourceLabel =
                  source === undefined
                    ? 'Metabase default'
                    : SOURCE_OPTIONS.find((o) => o.value === source)?.label ?? source;
                return (
                  <div
                    key={f.slug}
                    className="flex flex-col gap-1 text-xs py-1.5 border-b border-border/40 last:border-b-0"
                  >
                    <div className="flex items-center gap-2">
                      <code className="bg-muted text-muted-foreground rounded px-1 py-0.5 shrink-0 font-mono">
                        {f.slug}
                      </code>
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className="text-foreground truncate" title={f.name}>{f.name}</span>
                        <span className="text-[10px] text-muted-foreground">
                          {f.type}
                          {editable && (
                            <> · <span className="text-foreground/70">{sourceLabel}</span></>
                          )}
                        </span>
                      </div>
                      {editable && (
                        <Button
                          type="button"
                          size="sm"
                          variant={isOpen ? 'secondary' : 'outline'}
                          className="h-7 px-2 text-xs shrink-0"
                          onClick={() => toggleFilterOpen(f.slug)}
                        >
                          {isOpen ? (
                            <>
                              Hide <X className="h-2.5 w-2.5 ml-1" />
                            </>
                          ) : (
                            <>
                              Edit <Pencil className="h-2.5 w-2.5 ml-1" />
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                    {editable && isOpen && (
                      <div className="flex flex-col gap-1 pl-1 pt-1">
                        <Select
                          value={source ?? '__default__'}
                          onValueChange={(v) => {
                            if (v === '__default__') {
                              onFilterSourceChange?.(f.slug, undefined);
                              onFilterValueChange?.(f.slug, '');
                            } else {
                              const next = v as FilterValueSource;
                              onFilterSourceChange?.(f.slug, next);
                              if (next !== 'static') onFilterValueChange?.(f.slug, '');
                            }
                          }}
                        >
                          <SelectTrigger className="h-7 text-xs">
                            <SelectValue placeholder="Use Metabase default" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__default__">Use Metabase default</SelectItem>
                            {SOURCE_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {source === 'static' && (
                          <div className="space-y-1">
                            <Input
                              value={filterValues?.[f.slug] ?? ''}
                              onChange={(e) => onFilterValueChange?.(f.slug, e.target.value)}
                              placeholder={`Enter value for ${f.name}`}
                              className="h-7 text-xs"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
        </CardContent>
      </Card>
    </div>
  );
}
